/**
 * ✨ GAMME ÉTONNANTE - Les produits stars avec SONS !
 * 
 * Chaque produit a son propre son caractéristique au clic/survol :
 * - 🥤 Red Bull : PSHIIIT (canette qui s'ouvre)
 * - 🍸 Vodkas : TING! (tchin/trinquer)
 * - 🍷 Vins : POP (bouchon)
 * - 🍾 Champagne : POP! (bouchon qui saute fort + pétillement)
 * - 🥃 Whisky : CLIC (verre cristal)
 * - 🍺 Bières : PSHIT mousseux
 * - 🌿 CBD : AAAHHHH (soupir de satisfaction)
 * - 💨 Puffs : PROUT 😂 (pet sonore)
 * - 🐕 Croquettes : WOUF (petit chien)
 * - 🐱 Pâtées chats : MIAOU
 * - 🍪 Biscuits : CROC CROC
 * - 🥔 Chips : CRUNCH
 * - 🍬 Bonbons : POP déballage
 * - 🍦 Glaces : SLURP
 * - ❄️ Surgelés : SLURP
 * - 🍭 Barbe à papa : ZING magique
 * - 🧴 Hygiène : SQUISH
 * - 💄 Beauté : SIFFLEMENT 😏
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Volume2, VolumeX } from 'lucide-react';
import productSounds from '../../hooks/useProductSounds';

const {
  playRedBullSound,
  playVodkaSound,
  playWineSound,
  playChampagneSound,
  playWhiskySound,
  playBeerSound,
  playCBDSound,
  playPuffSound,
  playDogSound,
  playCatSound,
  playBiscuitSound,
  playChipsSound,
  playCandySound,
  playIceCreamSound,
  playCottonCandySound,
  playBeautySound,
  playHygieneSound,
  playHouseSound,
  playLemonadeSound
} = productSounds;

// 🎵 Liste des produits avec leurs sons et émojis
const PRODUCTS = [
  { emoji: '🥤', name: 'Red Bull', sound: playRedBullSound, color: '#00d4ff' },
  { emoji: '🍸', name: 'Vodkas', sound: playVodkaSound, color: '#c0c0c0' },
  { emoji: '🍷', name: 'Vins', sound: playWineSound, color: '#722F37' },
  { emoji: '🍾', name: 'Champagne', sound: playChampagneSound, color: '#FFD700' },
  { emoji: '🥃', name: 'Whisky', sound: playWhiskySound, color: '#D2691E' },
  { emoji: '🍺', name: 'Bières', sound: playBeerSound, color: '#F4A460' },
  { emoji: '🥤', name: 'Limonades & Sodas', sound: playLemonadeSound, color: '#7CFC00' },
  { emoji: '💧', name: 'Boissons', sound: playLemonadeSound, color: '#00BFFF' },
  { emoji: '🌿', name: 'CBD**', sound: playCBDSound, color: '#228B22' },
  { emoji: '💨', name: 'Puffs*', sound: playPuffSound, color: '#9966FF' },
  { emoji: '🐕', name: 'Croquettes petits chiens', sound: playDogSound, color: '#8B4513' },
  { emoji: '🐱', name: 'Pâtées pour chats', sound: playCatSound, color: '#FF6347' },
  { emoji: '🍪', name: 'Biscuits', sound: playBiscuitSound, color: '#DEB887' },
  { emoji: '🥔', name: 'Chips', sound: playChipsSound, color: '#FFD700' },
  { emoji: '🍬', name: 'Bonbons', sound: playCandySound, color: '#FF69B4' },
  { emoji: '🍦', name: 'Glaces', sound: playIceCreamSound, color: '#FFB6C1' },
  { emoji: '❄️', name: 'Surgelés', sound: playIceCreamSound, color: '#87CEEB' },
  { emoji: '🍭', name: 'Barbe à papa', sound: playCottonCandySound, color: '#FF1493' },
  { emoji: '🏠', name: 'Tout pour la maison', sound: playHouseSound, color: '#4169E1' },
  { emoji: '🧴', name: 'Hygiène', sound: playHygieneSound, color: '#00CED1' },
  { emoji: '💄', name: 'Beauté', sound: playBeautySound, color: '#FF1493' },
];

// 🎯 Composant individuel pour chaque produit
const ProductPill = ({ product, soundEnabled, onPlay }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  const handleClick = () => {
    if (soundEnabled && product.sound) {
      try {
        product.sound();
        setIsPlaying(true);
        onPlay?.(product.name);
        setTimeout(() => setIsPlaying(false), 500);
      } catch (e) {
        console.log('Sound error:', e);
      }
    }
  };

  return (
    <motion.button
      onClick={handleClick}
      className="group relative flex items-center gap-2 px-4 py-2.5 rounded-full transition-all cursor-pointer"
      style={{
        background: isPlaying 
          ? `linear-gradient(135deg, ${product.color}40, ${product.color}20)` 
          : 'rgba(255, 255, 255, 0.05)',
        border: isPlaying 
          ? `2px solid ${product.color}80` 
          : '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: isPlaying ? `0 0 20px ${product.color}40` : 'none'
      }}
      whileHover={{ 
        scale: 1.05, 
        backgroundColor: `${product.color}20`,
        borderColor: `${product.color}50`
      }}
      whileTap={{ scale: 0.95 }}
      animate={isPlaying ? {
        scale: [1, 1.1, 1],
        boxShadow: [`0 0 0px ${product.color}00`, `0 0 30px ${product.color}60`, `0 0 10px ${product.color}30`]
      } : {}}
      transition={{ duration: 0.3 }}
    >
      {/* Emoji avec animation */}
      <motion.span 
        className="text-xl"
        animate={isPlaying ? { 
          rotate: [0, -10, 10, -10, 0],
          scale: [1, 1.3, 1]
        } : {}}
        transition={{ duration: 0.4 }}
      >
        {product.emoji}
      </motion.span>
      
      {/* Nom du produit */}
      <span className="text-white font-medium text-sm whitespace-nowrap">
        {product.name}
      </span>

      {/* Indicateur sonore au survol */}
      {soundEnabled && (
        <motion.div
          className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-cyan-500 flex items-center justify-center opacity-0 group-hover:opacity-100"
          initial={{ scale: 0 }}
          whileHover={{ scale: 1 }}
        >
          <Volume2 className="w-2.5 h-2.5 text-white" />
        </motion.div>
      )}
    </motion.button>
  );
};

// 🎵 Section principale
const GammeEtonnanteSection = () => {
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastPlayed, setLastPlayed] = useState(null);

  return (
    <section className="relative py-16 px-4">
      {/* Header */}
      <div className="text-center mb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 mb-4"
        >
          <Sparkles className="w-5 h-5 text-purple-400" />
          <span className="text-purple-400 text-sm font-bold uppercase tracking-wider">Une Gamme Étonnante</span>
          
          {/* Toggle son */}
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="ml-2 p-1.5 rounded-full hover:bg-white/10 transition-colors"
            title={soundEnabled ? "Désactiver les sons" : "Activer les sons"}
          >
            {soundEnabled ? (
              <Volume2 className="w-4 h-4 text-cyan-400" />
            ) : (
              <VolumeX className="w-4 h-4 text-gray-500" />
            )}
          </button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-gray-400 text-sm"
        >
          {soundEnabled ? "" : "🔇 Sons désactivés"}
        </motion.p>
      </div>

      {/* Grille de produits */}
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-3"
        >
          {PRODUCTS.map((product, index) => (
            <motion.div
              key={product.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.03 }}
            >
              <ProductPill 
                product={product} 
                soundEnabled={soundEnabled}
                onPlay={setLastPlayed}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Sous-texte */}
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="text-center text-gray-500 text-xs mt-8"
      >
        ...et tout ce dont vous avez besoin au quotidien :-)
      </motion.p>
    </section>
  );
};

export default GammeEtonnanteSection;
